export class AnalyticsService {
  private static instance: AnalyticsService;
  private isEnabled: boolean = false;

  private constructor() {
    this.isEnabled = import.meta.env.VITE_ANALYTICS_ENABLED === 'true';
  }

  public static getInstance(): AnalyticsService {
    if (!AnalyticsService.instance) {
      AnalyticsService.instance = new AnalyticsService();
    }
    return AnalyticsService.instance;
  }

  public initialize(): void {
    if (!this.isEnabled) {
      console.log('Analytics disabled - not loading tracking scripts');
      return;
    }

    this.loadAnalyticsScripts();
  }

  private loadAnalyticsScripts(): void {
      // CookieBot Banner
      this.loadExternalScript('https://consent.cookiebot.com/uc.js', {
          'id': 'Cookiebot',
          'data-cbid': 'fc93cc8b-ca9a-4b46-97a4-955b493b8bb4',
          'data-blockingmode': 'auto',
          'type': 'text/javascript',
          'data-site': 'MRMKJQLE',
          'defer': 'true'
      });

      // CookieBot Declaration
      this.loadExternalScript('https://consent.cookiebot.com/fc93cc8b-ca9a-4b46-97a4-955b493b8bb4/cd.js', {
          'id': 'CookieDeclaration',
          'type': 'text/javascript',
          'async': 'true'
      });

    // Application Insights - Paste your script here exactly as received
    this.injectScript(`
      var appInsights = window.appInsights || function (a) {
          function b(a) { c[a] = function () { var b = arguments; c.queue.push(function () { c[a].apply(c, b) }) } } var c = { config: a }, d = document, e = window; setTimeout(function () { var b = d.createElement("script"); b.src = a.url || "https://az416426.vo.msecnd.net/scripts/a/ai.0.js", d.getElementsByTagName("script")[0].parentNode.appendChild(b) }); try { c.cookie = d.cookie } catch (a) { } c.queue = []; for (var f = ["Event", "Exception", "Metric", "PageView", "Trace", "Dependency"]; f.length;)b("track" + f.pop()); if (b("setAuthenticatedUserContext"), b("clearAuthenticatedUserContext"), b("startTrackEvent"), b("stopTrackEvent"), b("startTrackPage"), b("stopTrackPage"), b("flush"), !a.disableExceptionTracking) { f = "onerror", b("_" + f); var g = e[f]; e[f] = function (a, b, d, e, h) { var i = g && g(a, b, d, e, h); return !0 !== i && c["_" + f](a, b, d, e, h), i } } return c
      }({
          instrumentationKey: "b9a06a49-46aa-404e-bc6b-ca4c7f95e02a"
      });
      window.appInsights = appInsights, appInsights.queue && 0 === appInsights.queue.length && appInsights.trackPageView();
    `);

    // Google Analytics - Paste your script here exactly as received
    this.injectScript(`
      (function (i, s, o, g, r, a, m) {
      i['GoogleAnalyticsObject'] = r; i[r] = i[r] || function () {
          (i[r].q = i[r].q || []).push(arguments)
      }, i[r].l = 1 * new Date(); a = s.createElement(o),
          m = s.getElementsByTagName(o)[0]; a.async = 1; a.src = g; m.parentNode.insertBefore(a, m)
      })(window, document, 'script', 'https://www.google-analytics.com/analytics.js', 'ga');

      ga('create', 'UA-9262629-7', 'auto');
      ga('send', 'pageview');
    `);

    // Hotjar - Paste your script here exactly as received
    this.injectScript(`
      (function (h, o, t, j, a, r) {
          h.hj = h.hj || function () { (h.hj.q = h.hj.q || []).push(arguments) };
          h._hjSettings = { hjid: 466290, hjsv: 5 };
          a = o.getElementsByTagName('head')[0];
          r = o.createElement('script'); r.async = 1;
          r.src = t + h._hjSettings.hjid + j + h._hjSettings.hjsv;
          a.appendChild(r);
      })(window, document, '//static.hotjar.com/c/hotjar-', '.js?sv=');
    `);

    // Google Tag Manager - Paste your script here exactly as received
    this.injectScript(`
      (function (w, d, s, l, i) {
      w[l] = w[l] || []; w[l].push({
          'gtm.start':
              new Date().getTime(), event: 'gtm.js'
      }); var f = d.getElementsByTagName(s)[0],
          j = d.createElement(s), dl = l != 'dataLayer' ? '&l=' + l : ''; j.async = true; j.src =
              'https://www.googletagmanager.com/gtm.js?id=' + i + dl; f.parentNode.insertBefore(j, f);
      })(window, document, 'script', 'dataLayer', 'GTM-KSWDDTX');
    `);

    // Fathom Analytics - Paste your script here exactly as received
    this.loadExternalScript('https://cdn.usefathom.com/script.js', {
      'data-site': 'MRMKJQLE',
      'defer': 'true'
    });

    // MailChimp - Paste your script here exactly as received
    // temporaray disabled
    // this.loadExternalScript('//downloads.mailchimp.com/js/signup-forms/popup/unique-methods/embed.js', {
    //   'data-dojo-config': 'usePlainJson: true, isDebug: false'
    // }, () => {
    //   // This runs after MailChimp script loads
    //   this.injectScript(`
    //     window.dojoRequire(["mojo/signup-forms/Loader"], function (L) { 
    //       L.start({ "baseUrl": "mc.us10.list-manage.com", "uuid": "cace9db03e75960c357857b74", "lid": "148e14402a", "uniqueMethods": true }) 
    //     });
    //   `);
    // });
  }

  private injectScript(scriptContent: string): void {
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.text = scriptContent;
    document.head.appendChild(script);
  }

  private loadExternalScript(src: string, attributes?: Record<string, string>, onLoad?: () => void): void {
    const script = document.createElement('script');
    script.src = src;
    
    if (attributes) {
      Object.entries(attributes).forEach(([key, value]) => {
        script.setAttribute(key, value);
      });
    }

    if (onLoad) {
      script.onload = onLoad;
    }

    document.head.appendChild(script);
  }

  public isAnalyticsEnabled(): boolean {
    return this.isEnabled;
  }

  // Public methods for tracking events
  public trackEvent(eventName: string, properties?: any): void {
    if (!this.isEnabled) return;

    try {
      // Try to use available analytics services
      if ((window as any).appInsights && (window as any).appInsights.trackEvent) {
        (window as any).appInsights.trackEvent({ name: eventName, properties });
      }

      if ((window as any).ga) {
        (window as any).ga('send', 'event', 'Custom', eventName, properties);
      }

      if ((window as any).hj) {
        (window as any).hj('event', eventName);
      }
    } catch (error) {
      console.error('Failed to track event:', error);
    }
  }

  public trackPageView(pageName?: string): void {
    if (!this.isEnabled) return;

    try {
      // Try to use available analytics services
      if ((window as any).appInsights && (window as any).appInsights.trackPageView) {
        (window as any).appInsights.trackPageView({ name: pageName });
      }

      if ((window as any).ga) {
        (window as any).ga('send', 'pageview', pageName);
      }
    } catch (error) {
      console.error('Failed to track page view:', error);
    }
  }
}
